import { router } from '@inertiajs/react'
import { useState } from 'react'

import { csrfToken } from '../../../utils/csrfToken'

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="alert"
        aria-label="Close"
        onClick={onDismiss}
      ></button>
    </div>
  )
}

function ShareholderRow({ shareholder, index, onChange, onRemove, errors }) {
  const prefix = `shareholders[${index}]`
  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Shareholder {index + 1}</h6>
          <button type="button" className="btn btn-sm btn-danger" onClick={() => onRemove(index)}>
            Remove
          </button>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              value={shareholder.name || ''}
              onChange={(e) => onChange(index, 'name', e.target.value)}
            />
            {errors?.[`${prefix}.name`] ? (
              <div className="invalid-feedback d-block">{errors[`${prefix}.name`]}</div>
            ) : null}
          </div>
          <div className="col-md-6">
            <label className="form-label">Birthdate</label>
            <input
              type="date"
              className="form-control"
              value={shareholder.birthdate || ''}
              onChange={(e) => onChange(index, 'birthdate', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={shareholder.email || ''}
              onChange={(e) => onChange(index, 'email', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Contact Number</label>
            <input
              type="text"
              className="form-control"
              value={shareholder.contact_number || ''}
              onChange={(e) => onChange(index, 'contact_number', e.target.value)}
            />
          </div>
          <div className="col-md-8">
            <label className="form-label">Address</label>
            <input
              type="text"
              className="form-control"
              value={shareholder.address || ''}
              onChange={(e) => onChange(index, 'address', e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Shares Count</label>
            <input
              type="number"
              className="form-control"
              min="1"
              value={shareholder.shares_count || ''}
              onChange={(e) => onChange(index, 'shares_count', e.target.value)}
            />
            {errors?.[`${prefix}.shares_count`] ? (
              <div className="invalid-feedback d-block">{errors[`${prefix}.shares_count`]}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Edit({ gisRecord, errors }) {
  const [parValue, setParValue] = useState(String(gisRecord?.par_value ?? ''))
  const [totalSharesIssued, setTotalSharesIssued] = useState(String(gisRecord?.total_shares_issued ?? ''))
  const [shareholders, setShareholders] = useState(
    (gisRecord?.shareholders || []).map((s) => ({
      name: s.name || '',
      birthdate: s.birthdate || '',
      email: s.email || '',
      contact_number: s.contact_number || '',
      address: s.address || '',
      shares_count: s.shares_count ? String(s.shares_count) : '',
    }))
  )
  const [saving, setSaving] = useState(false)
  const [fieldErrors, setFieldErrors] = useState(errors && Object.keys(errors).length ? errors : null)
  const [flashAlert, setFlashAlert] = useState(null)

  function addShareholder() {
    setShareholders((prev) => [
      ...prev,
      { name: '', birthdate: '', email: '', contact_number: '', address: '', shares_count: '' },
    ])
  }

  function removeShareholder(index) {
    setShareholders((prev) => prev.filter((_, i) => i !== index))
  }

  function updateShareholder(index, field, value) {
    setShareholders((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  async function submit(e) {
    e.preventDefault()
    setSaving(true)
    setFlashAlert(null)
    setFieldErrors(null)

    const payload = {
      gis_record: {
        par_value: parValue,
        total_shares_issued: totalSharesIssued,
        shareholders: shareholders.map((s) => ({
          ...s,
          shares_count: s.shares_count ? parseInt(s.shares_count, 10) : 0,
        })),
      },
    }

    const response = await fetch('/executives/gis_record', {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken() || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
    })

    if (response.status === 200 || response.status === 204 || response.redirected) {
      router.visit('/executives/gis_record')
      return
    }

    if (response.status === 422) {
      const data = await response.json()
      setFieldErrors(data.errors && Object.keys(data.errors).length ? data.errors : null)
      if (data.message) {
        setFlashAlert(data.message)
      }
      setSaving(false)
      return
    }

    setFlashAlert(`Failed to update GIS: ${response.status}`)
    setSaving(false)
  }

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-end">
            <div className="col">
              <h6 className="header-pretitle">Executives</h6>
              <h1 className="header-title">Edit General Information Sheet</h1>
            </div>
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-link"
                onClick={() => router.visit('/executives/gis_record')}
              >
                &lt; Back
              </button>
            </div>
          </div>
        </div>
      </div>

      <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />

      <form onSubmit={submit}>
        <div className="row">
          <div className="col-12 col-lg-4">
            <div className="card">
              <div className="card-header">
                <h4 className="card-header-title">Company Details</h4>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Par Value</label>
                  <div className="input-group">
                    <span className="input-group-text">PHP</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      value={parValue}
                      onChange={(e) => setParValue(e.target.value)}
                    />
                  </div>
                  {fieldErrors?.par_value ? (
                    <div className="invalid-feedback d-block">{fieldErrors.par_value.join(', ')}</div>
                  ) : null}
                </div>

                <div className="form-group">
                  <label className="form-label">Total Shares Issued</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={totalSharesIssued}
                    onChange={(e) => setTotalSharesIssued(e.target.value)}
                  />
                  {fieldErrors?.total_shares_issued ? (
                    <div className="invalid-feedback d-block">{fieldErrors.total_shares_issued.join(', ')}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-8">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Shareholders</h4>
              <button type="button" className="btn btn-primary" onClick={addShareholder}>
                Add Shareholder
              </button>
            </div>

            {fieldErrors ? (
              <div className="alert alert-danger">
                <div className="mb-2">GIS was not saved. Please correct the errors below.</div>
                <ul className="mb-0">
                  {Object.values(fieldErrors).flat().map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {shareholders.map((s, index) => (
              <ShareholderRow
                key={index}
                shareholder={s}
                index={index}
                onChange={updateShareholder}
                onRemove={removeShareholder}
                errors={fieldErrors}
              />
            ))}

            {!shareholders.length ? (
              <div className="card">
                <div className="card-body text-center text-body-secondary py-4">
                  No shareholders added. Click "Add Shareholder" to begin.
                </div>
              </div>
            ) : null}

            <div className="mt-4">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save GIS'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
